import { useCallback, useMemo, useState } from "react";
import { useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { useAPI } from "../../providers/ApiProvider";
import { useProject } from "../../providers/ProjectProvider";
import { FF_UNSAVED_CHANGES, isFF } from "../../utils/feature-flags";
import { isEmptyString } from "../../utils/helpers";
import { askConfirm } from "../../utils/ask";
import { cn } from "../../utils/bem";
import { ConfigPage } from "../CreateProject/Config/Config";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { useTranslation } from "../../i18n/LocaleProvider";

export const LabelingSettings = () => {
  const { t } = useTranslation();
  const { project, fetchProject, updateProject } = useProject();
  const [config, setConfig] = useState("");
  const [essentialDataChanged, setEssentialDataChanged] = useState(false);
  const hasChanges = isFF(FF_UNSAVED_CHANGES) && config !== project.label_config;
  const api = useAPI();

  useUpdatePageTitle(createTitleFromSegments([project?.title, "Labeling Interface Settings"]));

  /**
   * Handle the "essential config change" guard the backend raises when the
   * new XML renames labels / changes tag names in a way that would orphan
   * existing annotations. The API returns 400 with ``annotations_to_delete``;
   * we show a confirm dialog naming the exact count, then either retry with
   * an explicit ack (deletes the annotations) or abort.
   */
  const withEssentialChangeGuard = useCallback(async (runSave) => {
    const result = await runSave(false);
    const errPayload = result?.response?.validation_errors ?? result?.response ?? result?.validation_errors ?? result;
    const essentialHit =
      errPayload?.essential_config_change ||
      (typeof errPayload?.detail === "string" && /invalidate \d+ existing annotation/i.test(errPayload.detail));
    if (!essentialHit) return result;

    const count = errPayload?.annotations_to_delete ?? "existing";
    const confirmed = await askConfirm({
      title: "Delete annotations to apply this config?",
      body: `This config change will invalidate ${count} annotation${count === 1 ? "" : "s"} and delete them to avoid data contamination. This cannot be undone.`,
      okText: "Delete & apply",
      danger: true,
    });
    if (!confirmed) return result;
    return await runSave(true);
  }, []);

  const saveConfig = useCallback(
    isFF(FF_UNSAVED_CHANGES)
      ? async () => {
          return await withEssentialChangeGuard(async (ack) => {
            const res = await updateProject({
              label_config: config,
              ...(ack ? { drop_annotations_ack: true } : {}),
            });
            if (res?.$meta?.ok) {
              // Backend can prettify the config, so we need to update it to have relevant hasChanges value
              setConfig(res.label_config);
              return true;
            }
            return res;
          });
        }
      : async () => {
          return await withEssentialChangeGuard(async (ack) => {
            const res = await api.callApi("updateProjectRaw", {
              params: { pk: project.id },
              body: {
                label_config: config,
                ...(ack ? { drop_annotations_ack: true } : {}),
              },
            });
            if (res.ok) return true;
            const error = await res.json();
            fetchProject();
            return { response: error };
          });
        },
    [project, config, withEssentialChangeGuard],
  );

  const projectAlreadySetUp = useMemo(() => {
    if (project.label_config) {
      const hasConfig = !isEmptyString(project.label_config);
      const configIsEmpty = project.label_config.replace(/\s/g, "") === "<View></View>";
      const hasTasks = project.task_number > 0;

      console.log({ hasConfig, configIsEmpty, hasTasks, project });
      return hasConfig && !configIsEmpty && hasTasks;
    }
    return false;
  }, [project]);

  // Fields the edited setup reads that the project's tasks do not all
  // have (from the last validation). Saving is allowed, since data can be
  // imported after the setup, but not without saying so.
  const [dataWarnings, setDataWarnings] = useState([]);

  const onSave = useCallback(async () => {
    if (dataWarnings.length) {
      const lines = dataWarnings.map(({ field, without, of }) =>
        without === of ? `No task has "${field}".` : `${without} of ${of} tasks have no "${field}".`,
      );
      const confirmed = await askConfirm({
        title: "Some tasks lack a field this setup shows",
        body: `${lines.join(" ")} Those tasks will show it empty until data with that field is imported.`,
        okText: "Save anyway",
      });
      if (!confirmed) return { detail: "The labeling setup was not saved.", validation_errors: lines };
    }
    return saveConfig();
  }, [essentialDataChanged, saveConfig, dataWarnings]);

  const onUpdate = useCallback((config) => {
    setConfig(config);
  }, []);

  const onValidate = useCallback((validation) => {
    setEssentialDataChanged(validation.config_essential_data_has_changed);
    setDataWarnings(validation.data_warnings ?? []);
  }, []);

  if (!project.id) return null;

  return (
    <div className={cn("settings-page").mod({ wide: true }).toClassName()}>
      <SettingsPageHeader
        title={`Labeling Interface${hasChanges ? " *" : ""}`}
        subtitle={t("pages.settings.labeling_settings.define_the_labels_widgets_and_layout_ann")}
      />
      <ConfigPage
        config={project.label_config}
        project={project}
        onUpdate={onUpdate}
        onSaveClick={onSave}
        onValidate={onValidate}
        hasChanges={hasChanges}
        hideBuiltInHeading
      />
    </div>
  );
};

LabelingSettings.title = "Labeling Interface";
LabelingSettings.path = "/labeling";
